import type { ApolloError } from 'apollo-client';
import { useEffect } from 'react';
import { useLazyQuery } from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql';

import { RelationsForContentQuery } from '../RelationsForContentQuery.experimentalgraphql';
import type { RelationsForContentQuery as RelationsForContentQueryInterface } from '../__types__/RelationsForContentQuery';

const isKnownError = (error: ApolloError) => {
	return error.graphQLErrors.some(
		(e) =>
			e.message === 'User does not have permission to view the relationship' ||
			e.message === 'Target entity does not exist',
	);
};

export const useContentRelations = (
	relationName: string,
	contentId: string,
	isUnpublishedDraft: boolean | null,
) => {
	const variables = {
		relationName,
		sourceType: 'user',
		targetKey: contentId,
		targetStatus: 'draft',
	};
	const [getRelations, { data, loading, error }] = useLazyQuery<RelationsForContentQueryInterface>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		RelationsForContentQuery,
		{
			variables,
		},
	);

	useEffect(() => {
		// If we're reverting rather than deleting
		if (!isUnpublishedDraft) {
			getRelations();
		}
	}, [isUnpublishedDraft, getRelations]);

	useEffect(() => {
		if (error && isKnownError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	return {
		relations: data?.experimentalRelationsForContent?.results ?? [],
		loading,
		error,
	};
};
